import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const relatedArticles = slug ? getRelatedArticles(slug, 3) : [];

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | JM Comic – 官方入口与完整使用指南`;
    } else {
      document.title = '文章未找到 | JM Comic';
    }
  }, [article]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-background-100 rounded-full mx-auto mb-4">
            <i className="ri-file-search-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">
            文章未找到
          </h1>
          <p className="text-foreground-600 mb-6">您访问的文章不存在或已被移除</p>
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回分类导航
          </Link>
        </div>
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: 'JM Comic',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: window.location.origin,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '分类导航',
        item: `${window.location.origin}/categories`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.categoryName,
        item: `${window.location.origin}/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: article.title,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      <Breadcrumb
        items={[
          { label: '分类导航', to: '/categories' },
          { label: article.categoryName, to: `/category/${article.category}` },
          { label: article.title },
        ]}
      />

      <article className="py-8 md:py-12">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-8">
            <span className="inline-block text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full mb-4 whitespace-nowrap">
              {article.categoryName}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="whitespace-nowrap">{article.author}</span>
              <span className="text-foreground-300">·</span>
              <span className="whitespace-nowrap">{article.publishDate}</span>
              <span className="text-foreground-300">·</span>
              <span className="whitespace-nowrap flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>
          </div>

          <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="article-content prose max-w-none"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="py-12 md:py-16 bg-background-100/50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedArticles.map((ra) => (
                <ArticleCard
                  key={ra.slug}
                  slug={ra.slug}
                  title={ra.title}
                  excerpt={ra.excerpt}
                  coverImage={ra.coverImage}
                  categoryName={ra.categoryName}
                  publishDate={ra.publishDate}
                  readTime={ra.readTime}
                  author={ra.author}
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}